// @flow
import { getFormElementValues, toastError } from 'bench-internal-theme';
import { Mutation, Query } from '@apollo/client/react/components';
import React from 'react';

import CreateOrEditPersonForm from './CreateOrEditPersonForm';
import {
  CREATE_PERSON,
  GET_ASSIGNED_PERSONS,
  GET_CLIENT_ROLES,
  UPDATE_PERSON,
  UPDATE_PERSON_CLIENT_ROLES,
} from './queries';
import mapClientRolesToOptions from './mapClientRolesToOptions';
import type { Person } from './types';

const handleUpdatePersonClientRolesError = () =>
  toastError(
    'Unable to assign new user',
    'The new user was not assigned to the client. Try assigning them from the search box.',
  );

const handleCreatePersonError = () =>
  toastError(
    'Unable to create new user',
    'An error occurred when trying to create the user. Double check their info and try again.',
  );

const handleUpdatePersonError = () =>
  toastError(
    'Unable to update user',
    'An error occurred when trying to update the user. Double check their info and try again.',
  );

const handleCreatePersonCompleted = (clientId, updatePersonClientRoles, createPersonResult) => {
  const {
    createPerson: { id, defaultClientRoleId },
  } = createPersonResult;
  return updatePersonClientRoles({
    variables: {
      clientId,
      personId: id,
      clientRoleIds: [defaultClientRoleId],
    },
  });
};

const handleFormSubmit = (createPerson, updatePerson, personId, submitEvent) => {
  submitEvent.preventDefault();
  const newPersonValues = getFormElementValues(submitEvent.currentTarget);
  if (personId) {
    updatePerson({
      variables: {
        person: newPersonValues,
        personId,
      },
    });
  } else {
    createPerson({
      variables: {
        person: newPersonValues,
      },
    });
  }
};

type CreateOrEditPersonFormContainerProps = {
  clientId: string,
  onPersonSaved: () => void,
  // When provided, this is an edit form instead of a create form
  person?: Person,
};

const CreateOrEditPersonFormContainer = ({
  clientId,
  onPersonSaved,
  person,
}: CreateOrEditPersonFormContainerProps) => (
  <Mutation
    mutation={UPDATE_PERSON_CLIENT_ROLES}
    onCompleted={onPersonSaved}
    onError={handleUpdatePersonClientRolesError}
    refetchQueries={[{ query: GET_ASSIGNED_PERSONS, variables: { clientId } }]}
  >
    {updatePersonClientRoles => (
      <Mutation
        mutation={CREATE_PERSON}
        onCompleted={createPersonResult =>
          handleCreatePersonCompleted(clientId, updatePersonClientRoles, createPersonResult)
        }
        onError={handleCreatePersonError}
      >
        {createPerson => (
          <Mutation
            mutation={UPDATE_PERSON}
            onCompleted={onPersonSaved}
            onError={handleUpdatePersonError}
            refetchQueries={[{ query: GET_ASSIGNED_PERSONS, variables: { clientId } }]}
          >
            {updatePerson => (
              <Query query={GET_CLIENT_ROLES}>
                {(getClientRoles: any) => {
                  if (getClientRoles.loading || getClientRoles.error) {
                    return null;
                  }

                  return (
                    <CreateOrEditPersonForm
                      clientRoleOptions={mapClientRolesToOptions(getClientRoles.data.clientRoles)}
                      onSubmit={submitEvent =>
                        handleFormSubmit(
                          createPerson,
                          updatePerson,
                          person && person.id,
                          submitEvent,
                        )
                      }
                      person={person}
                    />
                  );
                }}
              </Query>
            )}
          </Mutation>
        )}
      </Mutation>
    )}
  </Mutation>
);

CreateOrEditPersonFormContainer.defaultProps = {
  person: null,
};

export default CreateOrEditPersonFormContainer;
